/**
 * 環境判定ユーティリティ
 * 本番/ステージング/ローカル環境を判定し、それぞれに応じたスタイルを提供
 */

export type EnvType = 'production' | 'staging' | 'local';

const FORCE_PRODUCTION_STYLE_KEY = 'forceProductionStyle';
const FAVICON_DIR = '/images/favicon/';
const FAVICON_PATHS: Record<EnvType, string> = {
  production: `${FAVICON_DIR}favicon.ico`,
  staging: `${FAVICON_DIR}favicon_stg.ico`,
  local: `${FAVICON_DIR}favicon_local.ico`,
};

function setFaviconHref(href: string): void {
  if (typeof document === 'undefined') {
    return;
  }

  const links = document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]');
  if (links.length > 0) {
    links.forEach((link) => {
      link.href = href;
      link.type = 'image/x-icon';
    });
    return;
  }

  const newLink = document.createElement('link');
  newLink.rel = 'icon';
  newLink.type = 'image/x-icon';
  newLink.href = href;
  document.head.appendChild(newLink);
}

/**
 * 本番スタイル強制フラグを取得
 */
export function isForceProductionStyle(): boolean {
  try {
    return localStorage.getItem(FORCE_PRODUCTION_STYLE_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * 本番スタイル強制フラグを設定
 */
export function setForceProductionStyle(enabled: boolean): void {
  try {
    if (enabled) {
      localStorage.setItem(FORCE_PRODUCTION_STYLE_KEY, 'true');
    } else {
      localStorage.removeItem(FORCE_PRODUCTION_STYLE_KEY);
    }
  } catch {
    // localStorage が使用できない場合は無視
  }
}

/**
 * 現在の環境を判定
 */
export function getEnvironment(): EnvType {
  const hostname = window.location.hostname;
  if (hostname === 'ichigen-cho.com' || hostname.endsWith('.ichigen-cho.com')) {
    return 'production';
  }
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'local';
  }
  return 'staging';
}

/**
 * 環境ラベル
 */
export const envLabels: Record<EnvType, string> = {
  production: '本番',
  staging: 'STG',
  local: 'LOCAL',
};

/**
 * ステージング・ローカル用の共通スタイル（オーバーライド用）
 */
export const envOverrideStyles: Record<'staging' | 'local', { background: string; menuTint: string }> = {
  staging: {
    background: 'linear-gradient(135deg, #fa8c16 0%, #d46b08 100%)',
    menuTint: 'rgba(250, 140, 22, 0.08)',
  },
  local: {
    background: 'linear-gradient(135deg, #13c2c2 0%, #08979c 100%)',
    menuTint: 'rgba(19, 194, 194, 0.08)',
  },
};

/**
 * 環境に応じたスタイルを取得（本番はそのまま、それ以外はオーバーライド）
 * forceProductionStyle が有効な場合は本番スタイルを返す
 */
export function getEnvStyles(
  productionBackground: string,
  productionMenuTint: string = 'transparent'
): { background: string; menuTint: string; label: string; isProduction: boolean } {
  const env = getEnvironment();
  const hostname = window.location.hostname;
  const forceProduction = isForceProductionStyle();

  // 本番環境、または本番スタイル強制時
  if (env === 'production' || forceProduction) {
    return {
      background: productionBackground,
      menuTint: productionMenuTint,
      label: envLabels.production,
      isProduction: true,
    };
  }

  return {
    ...envOverrideStyles[env],
    label: `${envLabels[env]} | ${hostname}`,
    isProduction: false,
  };
}

/**
 * 環境に応じたファビコンを適用
 */
export function applyEnvironmentFavicon(): void {
  const env = getEnvironment();
  const forceProduction = isForceProductionStyle();
  const faviconEnv = forceProduction ? 'production' : env;
  setFaviconHref(FAVICON_PATHS[faviconEnv]);
}

